import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { ref, onValue, update, get } from 'firebase/database';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaFilePdf, FaImage, FaFileAlt, FaCheckCircle, FaChevronRight } from 'react-icons/fa';
import EngineerLayout from './EngineerLayout';

const EngineerEngineerDocuments = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [engineerData, setEngineerData] = useState(null);
    const [status, setStatus] = useState('');
    const [remarks, setRemarks] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (!user) {
                navigate('/login');
            } else {
                try {
                    const userSnap = await get(ref(db, `ekamwatapzp/users/${user.uid}`));
                    if (userSnap.exists()) {
                        setCurrentUser(userSnap.val());
                    } else {
                        const storedUser = localStorage.getItem('currentUser');
                        if (storedUser) setCurrentUser(JSON.parse(storedUser));
                    }
                } catch (error) {
                    console.error("Error fetching user role:", error);
                }
            }
        });

        if (id) {
            const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
            const unsubData = onValue(engineerRef, (snapshot) => {
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setEngineerData(data);
                } else {
                    toast.error("Engineer data not found");
                    navigate('/engineer/engineers');
                }
                setLoading(false);
            });

            return () => {
                unsubscribe();
                unsubData();
            };
        }
    }, [id, navigate]);

    // Update status and remarks when role or data is loaded
    useEffect(() => {
        if (engineerData && currentUser?.role) {
            const roleKey = currentUser.role.toLowerCase().replace(' ', '');
            const statusField = `${roleKey}Status`;
            const remarkField = `${roleKey}Remark`;
            
            setStatus(engineerData[statusField] || '');
            setRemarks(engineerData[remarkField] || '');
        }
    }, [engineerData, currentUser]);

    const handleUpdateStatus = async () => {
        if (!status) {
            toast.error("Please select a status");
            return;
        }
        if (!currentUser || !currentUser.role) {
            toast.error("User role not found. Please log in again.");
            return;
        }

        setUpdating(true);
        try {
            const roleKey = currentUser.role.toLowerCase().replace(' ', '');
            const statusField = `${roleKey}Status`;
            const remarkField = `${roleKey}Remark`;
            
            const engineerRef = ref(db, `ekamwatapzp/EngineerRegistrations/${id}`);
            const updates = {
                status: status, // Global status
                remarks: remarks, // Global remarks
                statusUpdatedAt: new Date().toISOString(),
                engineerstatus: currentUser.role,
                [statusField]: status,
                [remarkField]: remarks
            };

            await update(engineerRef, updates);
            toast.success("Status Updated Successfully!");
        } catch (error) {
            toast.error("Error updating status: " + error.message);
        } finally {
            setUpdating(false);
        }
    };

    const renderDocument = (url, label) => {
        if (!url) return <span className="text-gray-300 italic text-xs font-bold uppercase tracking-widest">No Document</span>;
        
        const isPdf = url.toLowerCase().includes('.pdf');
        
        return (
            <a 
                href={url} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-3 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all font-bold text-xs uppercase tracking-widest shadow-sm border border-blue-100 group"
            >
                {isPdf ? <FaFilePdf className="text-red-500 text-base" /> : <FaImage className="text-blue-500 text-base" />}
                <span>View Document</span>
                <FaChevronRight className="text-[10px] opacity-0 group-hover:opacity-100 transition-all translate-x-[-4px] group-hover:translate-x-0" />
            </a>
        );
    };

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-50/50">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#085e53]"></div>
                </div>
            </EngineerLayout>
        );
    }

    const documentList = [
        { name: "शासनमान्य संस्थेचे प्रमाणपत्र", url: engineerData?.documents?.['1']?.url },
        { name: "शासनमान्य संस्थेचे शेवटच्या वर्षाचे गुणपत्रक", url: engineerData?.documents?.['2']?.url },
        { name: "अधिवस प्रमाणपत्र", url: engineerData?.documents?.['3']?.url },
        { name: "नाव पत्ता असलेल्या कागदावर स्वतःचा फोटो लावून फोटो स्वसाक्षांकित करावा", url: engineerData?.documents?.['4']?.url },
        { name: "आय कार्ड साईझ फोटो", url: engineerData?.documents?.['5']?.url },
        { name: "अर्जदाराची स्वाक्षरी", url: engineerData?.documents?.['6']?.url },
        { name: "आधार कार्ड", url: engineerData?.documents?.['7']?.url },
        { name: "पॅन कार्ड", url: engineerData?.documents?.['8']?.url },
        { name: "प्रतिज्ञा पत्र", url: engineerData?.documents?.['9']?.url },
        { name: "रोजगार कार्ड", url: engineerData?.documents?.['10']?.url },
        { name: "DSC टोकन", url: engineerData?.documents?.['11']?.url },
        { name: "Other Documents", url: engineerData?.documents?.['12']?.url }
    ];

    const challanList = [
        { name: "नोंदणी चलन आणि फी चलन", url: engineerData?.challanFile || engineerData?.registrationChallanFile }
    ];

    return (
        <EngineerLayout>
            <div className="p-6 md:p-8 bg-gray-50/30 min-h-screen">
                {/* Dark Header Card */}
                <div className="bg-[#1a1c23] rounded-3xl p-10 mb-8 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl transition-transform duration-1000 group-hover:scale-110"></div>
                    
                    <div className="relative flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 bg-[#2d2f39] rounded-2xl flex items-center justify-center text-white border border-white/5 shadow-xl">
                                <FaFileAlt size={24} />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold text-white tracking-tight mb-2">अर्जदाराची कागदपत्रे</h1>
                                <p className="text-gray-400 font-bold text-sm tracking-widest uppercase">
                                    अर्जदार - <span className="text-blue-400 group-hover:text-blue-300 transition-colors uppercase">{engineerData?.firstName} {engineerData?.middleName} {engineerData?.surname}</span>
                                </p>
                            </div>
                        </div>
                        <button 
                            onClick={() => navigate(-1)}
                            className="bg-[#2d2f39] hover:bg-[#383a45] text-white px-8 py-3 rounded-xl text-sm font-bold tracking-widest transition-all hover:scale-105 active:scale-95 shadow-lg border border-white/5 flex items-center gap-3"
                        >
                            <FaArrowLeft size={14} /> मागे जा
                        </button>
                    </div>
                </div>

                <div className="space-y-8">
                    {/* Primary Documents Section */}
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 bg-white sticky top-0 z-10 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-5 w-1 bg-[#3b82f6] rounded-full"></div>
                                <h2 className="text-lg font-bold text-gray-800 tracking-tight">मुख्य दस्तऐवज (Primary Documents)</h2>
                            </div>
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] bg-gray-50 px-3 py-1 rounded-full">
                                एकूण {documentList.length} कागदपत्रे
                            </span>
                        </div>
                        
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50/50 border-b border-gray-100">
                                        <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center w-20">#</th>
                                        <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">कागदपत्राचे नाव (Document Name)</th>
                                        <th className="px-8 py-5 text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center w-48">कृती (Action)</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {documentList.map((doc, index) => (
                                        <tr key={index} className="group hover:bg-blue-50/30 transition-colors">
                                            <td className="px-8 py-6 text-center text-xs font-bold text-gray-400 italic">
                                                {String(index + 1).padStart(2, '0')}
                                            </td>
                                            <td className="px-8 py-6 font-bold text-gray-700 text-sm group-hover:text-blue-700 transition-colors">
                                                {doc.name}
                                            </td>
                                            <td className="px-8 py-6 text-center">
                                                {renderDocument(doc.url, doc.name)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Challan Section */}
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 bg-white flex items-center gap-3">
                            <div className="h-5 w-1 bg-[#f59e0b] rounded-full"></div>
                            <h2 className="text-lg font-bold text-gray-800 tracking-tight">चलन आणि फी (Challan & Fee)</h2>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <tbody className="divide-y divide-gray-50">
                                    {challanList.map((doc, index) => (
                                        <tr key={index} className="group hover:bg-amber-50/30 transition-colors">
                                            <td className="px-8 py-6 text-center text-xs font-bold text-gray-400 italic w-20">
                                                01
                                            </td>
                                            <td className="px-8 py-6 font-bold text-gray-700 text-sm group-hover:text-amber-700 transition-colors">
                                                {doc.name}
                                            </td>
                                            <td className="px-8 py-6 text-center w-48">
                                                {renderDocument(doc.url, doc.name)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Status Update Section */}
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                        <div className="px-8 py-6 border-b border-gray-50 bg-white flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="h-5 w-1 bg-[#10b981] rounded-full"></div>
                                <h2 className="text-lg font-bold text-gray-800 tracking-tight">अर्ज स्थिती अपडेट (Status Update)</h2>
                            </div>
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                Real-time Synchronization Active
                            </div>
                        </div>

                        <div className="p-8">
                            {/* Role Status Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
                                {[
                                    { label: 'Assistant', val: engineerData?.assistantStatus },
                                    { label: 'Accountant', val: engineerData?.accountantStatus },
                                    { label: 'Engineer', val: engineerData?.engineerStatus },
                                    { label: 'Admin', val: engineerData?.adminStatus },
                                    { label: 'CEO', val: engineerData?.ceoStatus },
                                    { label: 'ZP Head', val: engineerData?.zpheadStatus }
                                ].map((item, idx) => (
                                    <div key={idx} className="bg-gray-50/80 rounded-2xl p-4 border border-gray-100 flex flex-col items-center gap-2 hover:bg-white hover:shadow-lg transition-all duration-300">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">{item.label}</span>
                                        <div className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                            item.val === 'Approved' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 
                                            item.val === 'rejected' ? 'bg-red-50 text-red-600 border border-red-100' : 
                                            'bg-amber-50 text-amber-600 border border-amber-100'
                                        }`}>
                                            {item.val || 'Pending'}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                                {/* Form Controls */}
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">निवडलेली स्थिती (Selected Status)</label>
                                        <div className="relative">
                                            <select 
                                                value={status}
                                                onChange={(e) => setStatus(e.target.value)}
                                                className="w-full bg-gray-50 border border-gray-100 rounded-xl px-5 py-3.5 text-sm font-bold text-gray-700 shadow-sm transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none appearance-none"
                                            >
                                                <option value="">Select Status</option>
                                                <option value="Approved">Approved</option>
                                                <option value="rejected">Rejected</option>
                                                <option value="pending">Pending</option>
                                            </select>
                                            <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                                                <FaChevronRight className="rotate-90 text-xs" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">अभिप्राय (Remarks)</label>
                                        <textarea 
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                            rows="4"
                                            placeholder="Write your verification remarks here..."
                                            className="w-full bg-gray-50 border border-gray-100 rounded-xl px-5 py-4 text-sm font-bold text-gray-700 shadow-sm transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none placeholder:text-gray-300 placeholder:italic"
                                        ></textarea>
                                    </div>

                                    <div className="flex items-center gap-4 pt-4">
                                        <button 
                                            onClick={handleUpdateStatus}
                                            disabled={updating}
                                            className="flex-1 bg-[#085e53] hover:bg-[#064a41] text-white px-8 py-4 rounded-xl text-sm font-bold tracking-widest transition-all hover:scale-[1.02] active:scale-95 shadow-xl shadow-gray-200 disabled:opacity-50 flex items-center justify-center gap-3 uppercase"
                                        >
                                            {updating ? (
                                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                            ) : (
                                                <><FaCheckCircle /> Update Status</>
                                            )}
                                        </button>
                                        <button 
                                            onClick={() => navigate(-1)}
                                            className="px-8 py-4 rounded-xl text-sm font-bold text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all uppercase tracking-widest"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>

                                {/* Summary Sidebar */}
                                <div className="bg-gray-50/50 rounded-3xl p-8 border border-gray-100 space-y-6">
                                    <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.3em] mb-4">Verification Summary</h4>
                                    
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center py-3 border-b border-white">
                                            <span className="text-xs font-bold text-gray-500">Current Role:</span>
                                            <span className="text-xs font-bold text-[#1a1c23] bg-white px-3 py-1 rounded-lg border border-gray-100 uppercase tracking-widest shadow-sm">{currentUser?.role || '-'}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-3 border-b border-white">
                                            <span className="text-xs font-bold text-gray-500">Updated Date:</span>
                                            <span className="text-xs font-bold text-gray-700">
                                                {engineerData?.statusUpdatedAt ? new Date(engineerData.statusUpdatedAt).toLocaleDateString('en-GB') : '-'}
                                            </span>
                                        </div>
                                        <div className="pt-4">
                                            <div className="bg-[#1a1c23] rounded-2xl p-5 text-white shadow-xl">
                                                <div className="text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-1">System Application ID</div>
                                                <div className="text-sm font-bold font-mono tracking-wider">{id.toUpperCase()}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerEngineerDocuments;
